import type { MaskSchema } from "@frontend/pages/case-workspace/radiology/types"
import type { MaskDrawingConfig } from "@frontend/pages/case-workspace/radiology/viewer/types"

import { flattenMaskLabels } from "./mask-utils"

export interface BuildMaskDrawingConfigParams {
  activeModuleId: string | null | undefined
  activeMaskSchema: MaskSchema | null | undefined
  isActiveModuleEditable: boolean
  maskSeriesEligible: boolean
  activeMaskInstanceId: string | null
  brushSize: number
  filledPen: boolean
  eraseMode: boolean
  samMode: boolean
  samScribbleMode: boolean
  samConfidence: number
  drawingDisabled: boolean
}

/**
 * Build the NV scene mask drawing config. The config conflates two concerns:
 * rendering metadata (labels + projectModuleId) that the viewer needs to
 * hydrate overlays, and editing state (active instance, brush, SAM) that
 * drives drawing. We always provide rendering metadata when a mask module and
 * schema are present, and gate only the editing fields on edit-ability and
 * series eligibility — otherwise read-only contexts like review mode would
 * skip mask hydration entirely.
 */
export function buildMaskDrawingConfig(params: BuildMaskDrawingConfigParams): MaskDrawingConfig | null {
  const {
    activeModuleId,
    activeMaskSchema,
    isActiveModuleEditable,
    maskSeriesEligible,
    activeMaskInstanceId,
    brushSize,
    filledPen,
    eraseMode,
    samMode,
    samScribbleMode,
    samConfidence,
    drawingDisabled
  } = params

  if (!(activeModuleId && activeMaskSchema)) return null

  const canEdit = isActiveModuleEditable && maskSeriesEligible

  return {
    labels: flattenMaskLabels(activeMaskSchema).map((l) => ({ key: l.key, color: l.color })),
    activeMaskInstanceId: canEdit ? activeMaskInstanceId : null,
    brushSize,
    filledPen,
    eraseMode,
    samMode: canEdit ? samMode : false,
    samScribbleMode: canEdit ? samScribbleMode : false,
    samConfidence,
    drawingDisabled: !canEdit || drawingDisabled,
    disallowOverlap: activeMaskSchema.disallow_overlap ?? false,
    projectModuleId: activeModuleId
  }
}
